import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  Button,
  Card,
  CloseCircle,
  Divider,
  MediaMatch,
  Typography,
  useViewport,
} from '@lamimed/pantheon';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useGetProductBySlug } from 'hooks/useGetProductBySlug';
import { useRegisterDataLayer } from 'hooks/useRegisterDataLayer';
import { useSchedule } from 'hooks/useSchedule';
import { useScheduleStep } from 'hooks/useScheduleStep';

import { createTrackingIdPattern } from 'utils/createTrackingIdPattern';
import { trigger } from 'utils/events';
import { getQueryParamAsString } from 'utils/getQueryParamAsString';
import {
  whatsappMessage,
  whatsappNumber,
  whatsappUrl,
} from 'utils/whatsappUrl';

import { useProduct } from 'templates/Product/useProduct';
import { scheduleSteps } from 'templates/Schedule/shared/utils/scheduleSteps';

import { useCart } from '../CartDrawer/useCart';
import Notification from '../Notification';

import * as S from './styles';

export interface ProductHeroProps {
  imageFullsize?: string;
  image?: string;
}

export const ProductHeroVariant: React.FC<ProductHeroProps> = ({
  imageFullsize,
  image,
}) => {
  const { greaterThan } = useViewport();
  const router = useRouter();
  const [scrollDiscount, setScrollDiscount] = useState(0);

  const {
    query: { category, slug },
  } = useRouter();
  const { registerDataLayer } = useRegisterDataLayer();
  const { productFreeCancellationDisclaimer } = useFlags();
  const { goToScheduleStep } = useScheduleStep();

  const { fillProductInfo, setSelectedSubproduct, steps } = useSchedule();
  const { setIsCartOpen } = useCart();
  const { selectedSubproduct, scrollToSubproductAccordion } = useProduct();

  const { data } = useGetProductBySlug(getQueryParamAsString(slug));

  const clickButton = () => {
    registerDataLayer({
      event: 'product-cta-click',
      testName: 'product-hero-disclaimer-cancellation',
      variant: productFreeCancellationDisclaimer ? 'variant A' : 'variant B',
    });

    datadogRum.addAction('bookProductFromHero', {
      cart: {
        product: data?.slug,
        type: data?.attendenceType,
        quantity: 1,
      },
    });
  };

  const handleScheduleClick = () => {
    clickButton();

    if (data?.isTypology && !selectedSubproduct) {
      scrollToSubproductAccordion();

      trigger('subproductAccordion:open');

      return;
    }

    /**
     * TODO: Handle allowMultipleAppointments case for typologies.
     */
    if (data?.isTypology && selectedSubproduct) {
      fillProductInfo(selectedSubproduct.activityId, slug as string);

      setSelectedSubproduct(selectedSubproduct);

      const { index, path } = scheduleSteps.pickDate;

      goToScheduleStep({
        step: {
          index,
          path: `${path}?productId=${selectedSubproduct.activityId}&productSlug=${slug}`,
        },
        steps,
      });

      return;
    }

    if (typeof slug === 'string' && data) {
      fillProductInfo(data.activityId, slug);

      if (data.allowMultipleAppointments) {
        setIsCartOpen(true);

        return;
      }

      const { index, path } = scheduleSteps.pickDate;

      goToScheduleStep({
        step: {
          index,
          path: `${path}?productId=${data.activityId}&productSlug=${slug}`,
        },
        steps,
      });
    }
  };

  const returnPriceButton = (target: string) => {
    const prefix =
      data?.slug === 'supporto-infermiere-chat' ? 'Avvia Chat' : 'Prenota';

    if (!target) return `${prefix}`;

    if (target.toLowerCase().includes('gratis'))
      return `${prefix} ${target.toUpperCase()}`;

    return `${prefix} ${target.toLowerCase()}`;
  };

  const handleScroll = () => {
    const position = window.scrollY;

    if (position > 200) {
      setScrollDiscount(200);

      return;
    }

    setScrollDiscount(position);
  };

  const returnTrackingId = () => {
    switch (category) {
      case 'chat':
        return 'avvia-chat-desktop';
      case 'telemedicina':
        return 'prenota-telemedicina-desktop';
      default:
        return 'prenota-desktop';
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, false);

    return () => {
      window.removeEventListener('scroll', handleScroll, false);
    };
  }, []);

  return (
    <S.HeroWrapper scale={scrollDiscount / 10}>
      <Card
        variant="hero"
        image={
          greaterThan('tablet')
            ? imageFullsize || data?.imageFullsize
            : image || data?.image
        }
      >
        <Card.Body variant="hero">
          <S.BodyWrapper>
            <S.Row>
              <MediaMatch lessThan="tablet">
                <Typography variant="heading_4" color="text__light" as="h1">
                  {!productFreeCancellationDisclaimer &&
                    data?.slug !== 'supporto-infermiere-chat' && (
                      <Notification />
                    )}

                  {data?.activityTitle}
                </Typography>
              </MediaMatch>

              <MediaMatch greaterThan="tablet">
                <Typography variant="heading_1" color="text__light" as="h1">
                  {!productFreeCancellationDisclaimer &&
                    data?.slug !== 'supporto-infermiere-chat' && (
                      <Notification />
                    )}

                  {data?.activityTitle}
                </Typography>
              </MediaMatch>

              {data?.priceLabel && (
                <MediaMatch lessThan="tablet">
                  <Typography variant="heading_4" color="text__light">
                    {selectedSubproduct?.price || data.priceLabel || ''}
                  </Typography>
                </MediaMatch>
              )}
            </S.Row>

            <S.DescriptionWrapper>
              <S.Description color="text__light">
                {data?.description}
              </S.Description>

              <MediaMatch greaterThan="tablet">
                {data?.slug === 'supporto-infermiere-chat' ? (
                  <Link
                    href={whatsappUrl(
                      whatsappNumber.default,
                      whatsappMessage.default,
                    )}
                    passHref
                    legacyBehavior
                  >
                    <S.LinkProxy target="_blank">
                      <span
                        data-tracking-id={createTrackingIdPattern(
                          'product-hero-disclaimer-cancellation',
                          productFreeCancellationDisclaimer
                            ? 'variant-a'
                            : 'variant-b',
                        )}
                      >
                        <Button
                          variant="overlay"
                          size="large"
                          data-tracking-id={returnTrackingId()}
                          onClick={clickButton}
                        >
                          {returnPriceButton(data?.priceLabel || '')}
                        </Button>
                      </span>
                    </S.LinkProxy>
                  </Link>
                ) : (
                  <span
                    data-tracking-id={createTrackingIdPattern(
                      'product-hero-disclaimer-cancellation',
                      productFreeCancellationDisclaimer
                        ? 'variant-a'
                        : 'variant-b',
                    )}
                  >
                    <Button
                      variant="overlay"
                      size="large"
                      color="primary"
                      data-tracking-id={returnTrackingId()}
                      extended={greaterThan('tablet') ? false : true}
                      onClick={handleScheduleClick}
                    >
                      {returnPriceButton(
                        selectedSubproduct?.price || data?.priceLabel || '',
                      )}
                    </Button>
                  </span>
                )}
              </MediaMatch>
            </S.DescriptionWrapper>

            <MediaMatch lessThan="tablet">
              <S.IconCloseWrapper onClick={() => router.back()}>
                <CloseCircle width={35} height={35} color="light" />
              </S.IconCloseWrapper>
            </MediaMatch>
          </S.BodyWrapper>
        </Card.Body>
      </Card>

      <MediaMatch lessThan="tablet">
        <Divider position="below" />
      </MediaMatch>
    </S.HeroWrapper>
  );
};
